import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Plafonds du boost vidéo lus dans `app_settings`.
 * Aucune valeur métier n'est écrite en dur dans les composants : tant que le
 * chargement n'est pas terminé, `loading` reste vrai et l'UI attend.
 */
export interface BoostSettings {
  minDays: number;
  maxDays: number;
  defaultDays: number;
  maxActive: number;
  cooldownDays: number;
}

const KEYS = [
  "boost_duration_min_days",
  "boost_duration_max_days",
  "boost_duration_default_days",
  "boost_max_active_per_user",
  "boost_cooldown_days",
] as const;

export const useBoostSettings = () => {
  const [settings, setSettings] = useState<BoostSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", KEYS as unknown as string[]);

      if (cancelled) return;

      const read = (key: string): number | null => {
        const row = data?.find((r) => r.key === key);
        const parsed = Number(row?.value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      };

      const min = read("boost_duration_min_days");
      const max = read("boost_duration_max_days");
      const def = read("boost_duration_default_days");
      const maxActive = read("boost_max_active_per_user");
      const cooldown = read("boost_cooldown_days");

      if (min && max && def && maxActive && cooldown) {
        setSettings({
          minDays: min,
          maxDays: max,
          defaultDays: def,
          maxActive,
          cooldownDays: cooldown,
        });
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { settings, loading };
};

/** Liste des durées proposées (min → max, pas de 1 jour). */
export const durationChoices = (settings: BoostSettings): number[] => {
  const choices: number[] = [];
  for (let d = settings.minDays; d <= settings.maxDays; d += 1) choices.push(d);
  return choices;
};

/** Formatage date serveur en français (jj/mm/aaaa). */
export const formatBoostDate = (value: string | null | undefined): string => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("fr-FR");
};

/** Formatage date et heure serveur en français (jj/mm/aaaa à HH:MM). */
export const formatBoostDateTime = (value: string | null | undefined): string => {
  if (!value) return "";
  const date = new Date(value);
  const time = date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  return `${date.toLocaleDateString("fr-FR")} à ${time}`;
};

/** Formatage d'un montant en Coco : entier, séparateurs français (ex. 1 250). */
export const formatCoco = (value: number): string => Math.round(value).toLocaleString("fr-FR");

